import { signal } from "@preact/signals-react"


// Items the back has handed us this session. The queue grows on each
// load-items call and we never shrink it — when targetIdx outruns its
// length we fetch more, sending the keys we've already seen as
// `exclude` so the back doesn't keep returning the same rows.
export const items = signal([])

// Position within items[]. Validated/skipped items live behind this
// index so the UI can show progress (and so a "back" button could
// trivially restore them if we ever add one).
export const targetIdx = signal(0)

// Keys we've already loaded into the queue this session. Passed to the
// next load-items call as `exclude` to keep the back from serving the
// same items again after the operator skipped them.
export const seenKeys = signal([])

// True while a network request is in flight; the page uses it to grey
// out the action buttons so the operator can't double-submit.
export const isLoading = signal(false)

// Single-date filter (YYYY-MM-DD). Empty = all dates (default). When set,
// load-items only pulls from that day's tasks.
export const selectedDate = signal('')

// Retailer filter. Empty = all retailers (default). When set, load-items
// only pulls from that retailer's tasks.
export const selectedRetailerId = signal('')

// Section filter (id_section). Empty = all sections. Third level of the
// Date → Retailer → Section cascade; only meaningful once a retailer is
// chosen. Populated from /admin/review-queue/available/sections.
export const selectedSectionId = signal('')

// Section catalogue for the dropdown — sections with pending resources
// for the chosen date + retailer. Loaded by loadSections.
export const sections = signal([])

// IA-reason facet (normalized qa_reason bucket). Empty = all reasons.
// Orthogonal to the date/retailer/section cascade; composes with it.
export const selectedReason = signal('')

// Available reason buckets (+ counts) for the current scope. Loaded by
// loadReasons from /admin/review-queue/available/reasons.
export const reasons = signal([])

// Country filter (ISO-2, uppercase). Empty = all countries. Narrows the
// retailer dropdown to those in the country AND passes through to the
// back so unspecified-retailer queries still get scoped to the chosen
// market. Composes with selectedRetailerId.
export const selectedCountry = signal('')

// Retailer catalogue for the dropdown. Loaded once on mount.
export const retailers = signal([])

// Total pending-QA resources for the current scope (selected date +
// retailer, or all if none). Returned by load-items; shown as a counter.
export const totalPending = signal(0)

// Mirrors task.pipeline_stage / needs_qa_count returned by the validate
// endpoint so the UI can flash a "Section reviewed!" hint when an entire
// task is cleared by this validation.
export const lastValidatedTask = signal(null)

// Read-only task context echoed back by the backend when the queue is
// loaded with `?id_task=…` (Pipeline dashboard's per-task QA chip
// deep-link). Shape: { id_task, task_date, asset_path, section,
// retailer }. Null when the queue is not task-scoped. Powers the
// "Filtered by task …" banner.
export const taskContext = signal(null)


export function clearReviewQueueSignals() {
  items.value = []
  targetIdx.value = 0
  seenKeys.value = []
  isLoading.value = false
  lastValidatedTask.value = null
  selectedDate.value = ''
  selectedRetailerId.value = ''
  selectedSectionId.value = ''
  selectedReason.value = ''
  selectedCountry.value = ''
  retailers.value = []
  sections.value = []
  reasons.value = []
  totalPending.value = 0
  taskContext.value = null
}

// Resets only the queue cursor + seen-set, keeping the selected date.
// Used when the operator changes the date filter: we want a fresh pull
// for the new day but must not wipe the filter we're about to apply.
export function resetQueueKeepingDate() {
  items.value = []
  targetIdx.value = 0
  seenKeys.value = []
  lastValidatedTask.value = null
}
